<template>
  <div>
    <v-app-bar app color="primary" elevation="0">
      <v-container class="d-flex align-center">
        <v-app-bar-title class="d-flex align-center">
          <router-link to="/" class="text-decoration-none text-white d-flex align-center">
            <img src="/logo_white.png" alt="RankCrew Logo" class="logo mr-2" />
            <span class="font-weight-medium d-none d-md-inline my-auto">RankCrew</span>
          </router-link>
        </v-app-bar-title>
        
        <v-spacer></v-spacer>
        
        <!-- Navigation Links -->
        <div class="navigation-links d-none d-lg-flex">
          <v-btn to="/" class="mx-1" variant="text" rounded="xl">
            <v-icon class="mr-1">mdi-home</v-icon>
            Home
          </v-btn>
          <v-btn to="/about" class="mx-1" variant="text" rounded="xl">
            <v-icon class="mr-1">mdi-information</v-icon>
            About
          </v-btn>
          <v-btn to="/pricing" class="mx-1" variant="text" rounded="xl">
            <v-icon class="mr-1">mdi-currency-usd</v-icon>
            Pricing
          </v-btn>
          <v-btn to="/documentation" class="mx-1" variant="text" rounded="xl">
            <v-icon class="mr-1">mdi-book-open-variant</v-icon>
            Documentation
          </v-btn>
        </div>
        
        <!-- Auth Buttons -->
        <template v-if="isAuthenticated">
          <!-- Admin Section Menu - Only visible on large screens -->
          <div class="d-none d-lg-block">
            <v-btn
              ref="adminBtn"
              class="mx-2 admin-btn rounded-xl"
              variant="outlined"
              color="white"
              prepend-icon="mdi-view-dashboard"
            >
              <span class="font-weight-medium">Admin</span>
            </v-btn>
            <v-menu
              :activator="adminBtn"
              open-on-hover
              :openDelay="0"
              :closeDelay="0"
              eager
            >
              <v-list density="compact" bg-color="primary" dark class="rounded-xl submenu-list">
                <v-list-item to="/admin/dashboard" class="admin-menu-item" prepend-icon="mdi-view-dashboard" router>
                  <v-list-item-title>Dashboard</v-list-item-title>
                </v-list-item>
                <v-list-item to="/admin/planning" class="admin-menu-item" prepend-icon="mdi-calendar" router>
                  <v-list-item-title>Planning</v-list-item-title>
                </v-list-item>
                <v-list-item to="/admin/integrations" class="admin-menu-item" prepend-icon="mdi-link-variant" router>
                  <v-list-item-title>Integrations</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            
            <v-btn 
              color="white" 
              class="mx-2 rounded-xl" 
              variant="text" 
              prepend-icon="mdi-account-outline"
            >
              <span class="font-weight-medium">My Account</span>
              <v-menu activator="parent" open-on-hover :openDelay="100" :closeDelay="300">
                <v-list density="compact" bg-color="primary" dark class="rounded-xl submenu-list">
                  <v-list-item to="/my-account" class="admin-menu-item" prepend-icon="mdi-account-cog">
                    <v-list-item-title>Settings</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isAdmin" to="/admin/admin" class="admin-menu-item" prepend-icon="mdi-shield-account">
                    <v-list-item-title>Admin Dashboard</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="logout" class="admin-menu-item" prepend-icon="mdi-logout">
                    <v-list-item-title>Logout</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-btn>
          </div>
        </template>
        <template v-else>
          <!-- Auth buttons for non-authenticated users - Only visible on large screens -->
          <div class="auth-buttons d-none d-lg-flex">
            <v-btn 
              to="/login" 
              color="white" 
              variant="outlined" 
              class="mr-2 rounded-xl" 
              prepend-icon="mdi-login"
            >
              Login
            </v-btn>
            <v-btn 
              to="/register" 
              color="white" 
              variant="flat" 
              class="rounded-xl elevation-1"
              prepend-icon="mdi-account-plus"
            >
              Register
            </v-btn>
          </div>
        </template>
        
        <!-- Hamburger Menu Button - Visible on tablet and mobile -->
        <v-btn
          class="d-lg-none ml-4"
          icon
          color="white"
          @click="mobileMenu = !mobileMenu"
          aria-label="Menu"
        >
          <v-icon>{{ mobileMenu ? 'mdi-close' : 'mdi-menu' }}</v-icon>
        </v-btn>
      </v-container>
    </v-app-bar>
    
    <!-- Navigation Drawer for tablet and mobile -->
    <v-navigation-drawer
      v-model="mobileMenu"
      temporary
      location="right"
      width="300"
      class="rounded-l-xl pa-4"
    >
      <div class="d-flex flex-column h-100">
        <div class="text-h6 mb-6 text-primary font-weight-medium d-flex align-center">
          <img src="/logo.png" alt="RankCrew Logo" class="logo-small mr-2" />
          <span class="my-auto">RankCrew</span>
        </div>
        
        <v-list nav>
          <v-list-item to="/" prepend-icon="mdi-home" title="Home"></v-list-item>
          <v-list-item to="/about" prepend-icon="mdi-information" title="About"></v-list-item>
          <v-list-item to="/pricing" prepend-icon="mdi-currency-usd" title="Pricing"></v-list-item>
          <v-list-item to="/documentation" prepend-icon="mdi-book-open-variant" title="Documentation"></v-list-item>
          
          <v-divider class="my-4"></v-divider>
          
          <v-list-item v-if="!isAuthenticated" to="/login" prepend-icon="mdi-login" title="Login"></v-list-item>
          <v-list-item v-if="!isAuthenticated" to="/register" prepend-icon="mdi-account-plus" title="Register"></v-list-item>
          
          <template v-if="isAuthenticated">
            <!-- Admin section with expandable submenu -->
            <v-list-group value="admin">
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  prepend-icon="mdi-view-dashboard"
                  title="Admin"
                  class="font-weight-medium"
                ></v-list-item>
              </template>
              
              <v-list-item to="/admin/dashboard" prepend-icon="mdi-view-dashboard" title="Dashboard" class="pl-4"></v-list-item>
              <v-list-item to="/admin/planning" prepend-icon="mdi-calendar" title="Planning" class="pl-4"></v-list-item>
              <v-list-item to="/admin/integrations" prepend-icon="mdi-link-variant" title="Integrations" class="pl-4"></v-list-item>
            </v-list-group>
            
            <!-- My Account section with expandable submenu -->
            <v-list-group value="account">
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  prepend-icon="mdi-account-outline"
                  title="My Account"
                  class="font-weight-medium"
                ></v-list-item>
              </template>
              
              <v-list-item to="/my-account" prepend-icon="mdi-account-cog" title="Settings" class="pl-4"></v-list-item>
              <v-list-item v-if="isAdmin" to="/admin/admin" prepend-icon="mdi-shield-account" title="Admin Dashboard" class="pl-4"></v-list-item>
              <v-list-item @click="logout" prepend-icon="mdi-logout" title="Logout" class="pl-4"></v-list-item>
            </v-list-group>
            
            <!-- Other user menu items -->
            <v-list-item to="/dashboard" prepend-icon="mdi-home-variant" title="User Dashboard"></v-list-item>
            <v-list-item to="/calendar" prepend-icon="mdi-calendar" title="Calendar"></v-list-item>
            <v-list-item to="/subscription" prepend-icon="mdi-credit-card" title="Subscription"></v-list-item>
            <v-list-item to="/integrations" prepend-icon="mdi-link-variant" title="Integrations"></v-list-item>
          </template>
        </v-list>
        
        <v-spacer></v-spacer>
        
        <div class="text-caption text-center text-grey mt-4">
          &copy; {{ new Date().getFullYear() }} RankCrew
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { authService } from '../services/authService';
import { useAuthStore } from '../stores/auth';

export default {
  name: 'Header',
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();
    const mobileMenu = ref(false);
    const adminBtn = ref(null);
    
    // Use computed property to dynamically check auth status
    const isAuthenticated = computed(() => authStore.isAuthenticated);
    
    // Add isAdmin computed property
    const isAdmin = computed(() => authStore.user?.is_admin === true);
    
    onMounted(async () => {
      // Validate session to ensure token is still valid
      if (authStore.token) {
        await authStore.validateSession();
      }
    });

    const logout = async () => {
      await authService.logout();
      mobileMenu.value = false;
      router.push('/login');
    };

    return {
      isAuthenticated,
      isAdmin,
      logout,
      mobileMenu,
      adminBtn
    };
  }
};
</script>

<style scoped>
.v-btn {
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.5px;
}

.admin-btn {
  border: 1px solid white !important;
}

.navigation-links {
  margin-right: 16px;
}

.auth-buttons {
  display: flex;
}

.admin-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Remove all menu transitions, transforms, and apparition effects */
:deep(.v-menu__content),
:deep(.v-menu-transition-enter-active),
:deep(.v-menu-transition-leave-active),
:deep(.v-menu-transition-enter-from),
:deep(.v-menu-transition-leave-to) {
  transition: none !important;
  opacity: 1 !important;
  animation: none !important;
  transform: none !important;
  left: initial !important;
  top: initial !important;
  will-change: auto !important;
  translate: none !important;
  position: static !important;
  z-index: auto !important;
}

.rounded-xl {
  border-radius: 16px !important;
}

.text-primary {
  color: #2E7D32 !important;
}

.logo {
  height: 40px;
  width: auto;
  object-fit: contain;
  margin-bottom: 8px;
}

.logo-small {
  height: 32px;
  width: auto;
  object-fit: contain;
  margin-bottom: 8px;
}
</style> 